import { useEffect, useState } from "react";
import { Trash2, MailOpen, Mail, Reply } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Modal, StatusBadge, thCls, tdCls } from "./ui";

const STATUSES = ["new", "read", "replied"];

export default function Messages() {
  const [msgs, setMsgs] = useState(null);
  const [active, setActive] = useState(null);

  const load = () => api.get("/admin/contact-messages").then((r) => setMsgs(r.data || [])).catch(() => setMsgs([]));
  useEffect(() => { load(); }, []);

  const patch = async (id, body, msg) => {
    await api.patch(`/admin/contact-messages/${id}`, body).catch((e) => toast.error(formatApiError(e)));
    if (msg) toast.success(msg);
    load();
  };

  const open = (m) => {
    setActive(m);
    if (m.status === "new") patch(m.id, { status: "read" });
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    await api.delete(`/admin/contact-messages/${id}`).catch(() => {});
    setActive(null);
    load();
  };

  const fmt = (d) => {
    try { return new Date(d).toLocaleString(); } catch { return d || "—"; }
  };

  return (
    <div data-testid="admin-messages">
      <div className="flex items-center gap-3 mb-6 bg-gold-light border border-gold/40 rounded-xl px-5 py-4">
        <Mail className="w-5 h-5 text-gold-dark shrink-0" />
        <p className="text-sm text-clay">
          Messages sent through the Contact Us page appear here. Opening a message marks it as <strong>read</strong> — reply by email, then mark it <strong>replied</strong>.
        </p>
      </div>
      <div className="bg-white rounded-xl border border-[#E6DED3] shadow-sm overflow-x-auto">
        <table className="w-full text-left min-w-[860px]" data-testid="messages-table">
          <thead className="border-b border-sand">
            <tr>
              <th className={thCls}>From</th><th className={thCls}>Subject</th><th className={thCls}>Message</th>
              <th className={thCls}>Received</th><th className={thCls}>Status</th><th className={thCls}></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-sand">
            {(msgs || []).map((m) => (
              <tr key={m.id} data-testid={`message-row-${m.id}`} className={m.status === "new" ? "bg-gold-light/40" : ""}>
                <td className={tdCls}>
                  <p className="font-semibold">{m.name}</p>
                  <a href={`mailto:${m.email}`} className="text-xs text-ruby">{m.email}</a>
                  {m.business_name && <p className="text-xs text-stone2">{m.business_name}</p>}
                </td>
                <td className={`${tdCls} text-xs font-semibold`}>{m.subject}</td>
                <td className={`${tdCls} text-xs max-w-[280px]`}>
                  <p className="line-clamp-2">{m.message}</p>
                </td>
                <td className={`${tdCls} text-xs whitespace-nowrap`}>{fmt(m.created_at)}</td>
                <td className={tdCls}>
                  <select
                    value={m.status}
                    onChange={(e) => patch(m.id, { status: e.target.value }, "Status updated.")}
                    className="px-2 py-1.5 rounded-lg bg-sand/60 text-xs font-semibold outline-none"
                    data-testid={`message-status-${m.id}`}
                  >
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className={tdCls}>
                  <div className="flex items-center gap-1">
                    <button onClick={() => open(m)} className="p-2 text-navy-royal hover:bg-sand rounded-lg" data-testid={`view-message-${m.id}`} aria-label="View message">
                      <MailOpen className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(m.id)} className="p-2 text-ruby hover:bg-ruby-light rounded-lg" data-testid={`delete-message-${m.id}`} aria-label="Delete message">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {msgs && msgs.length === 0 && <p className="px-5 py-10 text-sm text-stone2 text-center">No contact messages yet.</p>}
      </div>

      {active && (
        <Modal title={active.subject} onClose={() => setActive(null)}>
          <div className="space-y-3" data-testid="message-detail">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-ink">{active.name}</p>
                <a href={`mailto:${active.email}`} className="text-sm text-ruby">{active.email}</a>
                {active.business_name && <p className="text-xs text-stone2 mt-0.5">{active.business_name}</p>}
              </div>
              <StatusBadge status={active.status} />
            </div>
            <p className="text-xs text-stone2">{fmt(active.created_at)}</p>
            <p className="text-sm text-clay leading-relaxed whitespace-pre-wrap bg-sand/50 rounded-xl p-4">{active.message}</p>
            <div className="flex items-center gap-3 pt-2">
              <a
                href={`mailto:${active.email}?subject=Re: ${encodeURIComponent(active.subject)}`}
                className="inline-flex items-center gap-1.5 bg-gold hover:bg-gold-dark text-navy text-xs font-bold px-4 py-2.5 rounded-full transition-colors"
                data-testid="message-reply-btn"
              >
                <Reply className="w-3.5 h-3.5" /> Reply by email
              </a>
              <button
                onClick={() => { patch(active.id, { status: "replied" }, "Marked as replied."); setActive(null); }}
                className="border border-[#E6DED3] text-ink text-xs font-semibold px-4 py-2.5 rounded-full hover:border-gold transition-colors"
                data-testid="message-mark-replied-btn"
              >
                Mark replied
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
